import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import { useHistory } from 'react-router'
import styled from 'styled-components'

import { FlexBox } from '../../components/Base/Base'
import { CopyToClipBoard } from '../../components/CopyToClipBoard/CopyToClipBoard'
import { TrailerIcon } from '../../components/Icons/Icon'
import { Text } from '../../components/Text/Text'
import Tooltip, { TOOLTIP_DIRECTIONS } from '../../components/Tooltip/Tooltip'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { useTeamTrailersV3 } from '../../hooks/query/useTeamTrailersV3'
import { useTeamTrucksV3 } from '../../hooks/query/useTeamTrucksV3'
import { EquipmentStatus } from '../../models/equipment'
import { TeamV3 } from '../../models/team'
import { Trailer } from '../../models/trailer'
import { Truck } from '../../models/truck'
import { blueGray, darkGray, errorD1, lightGray1, midGray, successD1, white } from '../../theme/colors'
import { label4 } from '../../theme/typography'
import { OdometerReadingText } from './styles'

interface EquipmentSectionProps {
  team: TeamV3
}

export function EquipmentSection({ team }: EquipmentSectionProps) {
  const { trucks } = useTeamTrucksV3(team?.id, { enabled: !!team?.id })
  const { trailers } = useTeamTrailersV3(team?.id, { enabled: !!team?.id })

  // Sort by active status first
  return (
    <FlexBox isColumn>
      {[...(trucks ?? [])]
        .sort((a, b) =>
          a.status === EquipmentStatus.ACTIVE ? -1 : b.status === EquipmentStatus.ACTIVE ? -1 : 1
        )
        .map((truck) => (
          <EquipmentItem key={truck.id} truck={truck} teamId={team.id} />
        ))}
      {[...(trailers ?? [])]
        .sort((a, b) =>
          a.status === EquipmentStatus.ACTIVE ? -1 : b.status === EquipmentStatus.ACTIVE ? -1 : 1
        )
        .map((trailer) => (
          <EquipmentItem key={trailer.id} trailer={trailer} teamId={team.id} />
        ))}
    </FlexBox>
  )
}

function EquipmentItem({ truck, trailer, teamId }: { truck?: Truck; trailer?: Trailer; teamId: string }) {
  const history = useHistory()
  const equipment = truck || trailer
  if (!equipment) return null

  return (
    <EquipmentSectionItem alignItems="stretch">
      <FlexBox isFullWidth>
        <FlexBox mr={2}>
          {truck && <TruckIcon />}
          {trailer && <img src={TrailerIcon} width="24" height="24" alt="" />}
        </FlexBox>

        <FlexBox isColumn gap="8px" flex={1}>
          {!!truck && (
            <CopyToClipBoard text={equipment.vinNumber!} textProps={{ variant: 'header3' }}>
              <Text variant="header3" color={darkGray}>
                VIN: {equipment.vinNumber}
              </Text>
            </CopyToClipBoard>
          )}
          {!!trailer && (
            <CopyToClipBoard
              text={`${equipment.vehicleYear} ${equipment.vehicleMake} ${equipment.vehicleModel}`}
              textProps={{ variant: 'header3' }}
            >
              <Text variant="header3" color={darkGray}>
                {equipment.vehicleYear} {equipment.vehicleMake} {equipment.vehicleModel}
              </Text>
            </CopyToClipBoard>
          )}

          {!!truck && (
            <CopyToClipBoard
              text={`${equipment.vehicleYear} ${equipment.vehicleMake} ${equipment.vehicleModel}`}
              textProps={{ variant: 'label2' }}
            >
              <Text variant="label2" color={darkGray}>
                {equipment.vehicleYear} {equipment.vehicleMake} {equipment.vehicleModel}
              </Text>
            </CopyToClipBoard>
          )}
          {!!trailer && (
            <CopyToClipBoard text={equipment.vinNumber!} textProps={{ variant: 'label2' }}>
              <Text variant="label2" color={darkGray}>
                VIN: {equipment.vinNumber}
              </Text>
            </CopyToClipBoard>
          )}

          {truck && (
            <CopyToClipBoard text={truck.eldVehicles?.[0]?.providerName!} textProps={{ variant: 'label2' }}>
              <Text variant="label2" color={darkGray}>
                Vehicle ID: {truck.eldVehicles?.[0]?.providerName}
              </Text>
            </CopyToClipBoard>
          )}
          {truck && truck.odometerReadingMi && (
            <Text variant="header4" color={lightGray1} mt={1}>
              Odometer Reading (Miles):
              <OdometerReadingText showErrorColor={truck.odometerReadingMi > 400000} errorColor={errorD1}>
                {' '}
                {truck.odometerReadingMi}
                {truck.odometerReadingMiDate && (
                  <> (Updated: {new Date(truck.odometerReadingMiDate).toLocaleDateString()})</>
                )}
              </OdometerReadingText>
            </Text>
          )}
        </FlexBox>

        <FlexBox gap="8px" justifyContent="flex-end">
          {equipment.status === EquipmentStatus.ACTIVE && (
            <Text variant="header3" color={successD1}>
              Active
            </Text>
          )}
          {equipment.status === EquipmentStatus.INACTIVE && (
            <Text variant="header3" color={lightGray1}>
              Inactive
            </Text>
          )}

          <FlexBox gap="8px">
            {truck?.assignedDrivers?.map((driver) => (
              <UserTooltip
                key={driver.id}
                content={<UserTooltipContent>{`${driver.firstName} ${driver.lastName}`}</UserTooltipContent>}
                direction={TOOLTIP_DIRECTIONS.UP}
              >
                <UserAvatar
                  width="24px"
                  height="24px"
                  borderWidth="1px"
                  fontSize="9px"
                  userName={`${driver.firstName} ${driver.lastName}`}
                />
              </UserTooltip>
            ))}
          </FlexBox>
        </FlexBox>
        <ArrowContainer onClick={() => history.push(`/teams/${teamId}/show/equipment`)}>
          <KeyboardArrowRightIcon htmlColor={blueGray} />
        </ArrowContainer>
      </FlexBox>
    </EquipmentSectionItem>
  )
}

const EquipmentSectionItem = styled(FlexBox)`
  padding: 24px 0 24px 40px;
  background-color: ${white};
  margin-bottom: 2px;
`

const UserTooltip = styled(Tooltip)`
  .react-tooltip-lite {
    border-radius: 3px;
    background: ${midGray};
  }

  .react-tooltip-lite-arrow {
    border-top-color: ${midGray};
  }
`

const UserTooltipContent = styled(FlexBox)`
  ${label4};
  color: ${white};
  padding: 2px 4px;
  justify-content: center;
  align-items: center;
`

const TruckIcon = styled(LocalShippingIcon)`
  width: 24px;
  height: 24px;
  color: ${blueGray};
`

const ArrowContainer = styled(FlexBox)`
  cursor: pointer;
  width: 100px;
  height: calc(100% + 48px);
  margin: -24px 0;
  padding: 24px 40px 0 0;
  justify-content: flex-end;
`
